import React, { useState } from 'react';
import { Icon, EmptyState, Badge } from '../components/UI';
import { ProgrammeCard, CourseCard } from '../components/Cards';
import { useApp } from '../context/AppContext';

export function StudentHome({ setActiveTab }) {
  const { user, data } = useApp();
  const myProgrammes = data.programmes.filter(p => user.programmes.includes(p.id));
  const totalCourses = myProgrammes.reduce((a, p) => a + p.courses.length, 0);
  const liveCourses = myProgrammes.reduce((a, p) => a + p.courses.filter(c => c.link).length, 0);
  const unread = data.emails.filter(e => e.to.includes(user.id) && !e.read[user.id]).length;

  return (
    <div className="page-enter" style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 28px' }}>
      <div style={{ marginBottom: 44 }}>
        <p style={{ fontSize: 11, fontWeight: 800, color: 'var(--orange)', letterSpacing: '0.14em', textTransform: 'uppercase', margin: '0 0 10px', fontFamily: 'var(--font-heading)' }}>Welcome back, {user.name.split(' ')[0]}</p>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 38, fontWeight: 800, color: 'var(--dark)', margin: '0 0 12px', letterSpacing: '-0.03em' }}>Everything AlgoAtWork,<br />in one place.</h1>
        <p style={{ fontSize: 16, color: 'var(--dark-60)', margin: 0, lineHeight: 1.6 }}>Systems, programmes, and the courses you need — all behind a single sign-in.</p>
      </div>

      {unread > 0 && (
        <div onClick={() => setActiveTab('Inbox')} style={{ background: 'var(--orange-soft)', border: '1.5px solid rgba(236,125,42,0.25)', borderRadius: 'var(--radius-lg)', padding: '14px 20px', marginBottom: 28, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
          <Icon name="mail" size={18} color="var(--orange)" />
          <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--orange)', margin: 0, fontFamily: 'var(--font-heading)' }}>
            You have {unread} unread message{unread > 1 ? 's' : ''} from your coordinator
          </p>
          <Icon name="chevronRight" size={14} color="var(--orange)" style={{ marginLeft: 'auto' }} />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 44 }}>
        {[
          { label: 'Programmes', value: myProgrammes.length, icon: 'award', color: 'var(--orange)' },
          { label: 'Courses', value: totalCourses, icon: 'book', color: 'var(--info)' },
          { label: 'Live links', value: liveCourses, icon: 'external', color: 'var(--success)' },
        ].map(stat => (
          <div key={stat.label} style={{ background: 'var(--white)', border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: stat.color.replace(')', ', 0.12)').replace('var(', 'rgba(').replace(/var\((.+)\)/, (_, v) => v === '--orange' ? 'rgba(236,125,42,0.12)' : v === '--info' ? 'rgba(47,111,176,0.12)' : 'rgba(46,139,87,0.12)'), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name={stat.icon} size={22} color={stat.color} />
            </div>
            <div>
              <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--dark)', lineHeight: 1, fontFamily: 'var(--font-heading)' }}>{stat.value}</div>
              <div style={{ fontSize: 13, color: 'var(--dark-60)', marginTop: 2 }}>{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 800, color: 'var(--dark)', margin: 0, letterSpacing: '-0.01em' }}>My Programmes</h2>
        <button onClick={() => setActiveTab('My Programmes')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 14, color: 'var(--dark-60)', fontFamily: 'var(--font-heading)', fontWeight: 600 }}>
          View all <Icon name="chevronRight" size={14} />
        </button>
      </div>

      {myProgrammes.length === 0 ? (
        <EmptyState icon="book" title="No programmes yet" message="Contact your administrator to be enrolled in a programme." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px,1fr))', gap: 20 }}>
          {myProgrammes.map(p => <ProgrammeCard key={p.id} programme={p} />)}
        </div>
      )}
    </div>
  );
}

export function StudentProgrammes() {
  const { user, data } = useApp();
  const myProgrammes = data.programmes.filter(p => user.programmes.includes(p.id));
  const [activeTab, setActiveTab] = useState(myProgrammes[0]?.id || null);
  const activeProg = myProgrammes.find(p => p.id === activeTab) || myProgrammes[0];

  if (myProgrammes.length === 0) {
    return (
      <div className="page-enter" style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 28px' }}>
        <EmptyState icon="book" title="No programmes yet" message="Contact your administrator to be enrolled in a programme." />
      </div>
    );
  }

  return (
    <div className="page-enter" style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 28px' }}>
      <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 30, fontWeight: 800, color: 'var(--dark)', margin: '0 0 6px', letterSpacing: '-0.02em' }}>My Programmes</h1>
      <p style={{ fontSize: 14, color: 'var(--dark-60)', margin: '0 0 32px' }}>You're enrolled in {myProgrammes.length} programme{myProgrammes.length !== 1 ? 's' : ''}.</p>

      <div style={{ display: 'flex', gap: 6, marginBottom: 36, borderBottom: '1px solid var(--dark-10)', paddingBottom: 0, flexWrap: 'wrap' }}>
        {myProgrammes.map(p => (
          <button
            key={p.id}
            onClick={() => setActiveTab(p.id)}
            style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: activeTab === p.id ? 700 : 500, padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer', color: activeTab === p.id ? 'var(--dark)' : 'var(--dark-60)', borderBottom: activeTab === p.id ? `3px solid ${p.color}` : '3px solid transparent', marginBottom: -1, display: 'flex', alignItems: 'center', gap: 7, transition: 'all 0.15s' }}
          >
            <Icon name={p.icon} size={15} color={activeTab === p.id ? p.color : 'var(--dark-40)'} />
            {p.name}
          </button>
        ))}
      </div>

      {activeProg && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: activeProg.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name={activeProg.icon} size={26} color={activeProg.color} />
            </div>
            <div>
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 700, color: 'var(--dark)', margin: '0 0 2px' }}>{activeProg.name}</h2>
              <p style={{ fontSize: 13, color: 'var(--dark-60)', margin: 0 }}>{activeProg.description}</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 16 }}>
            {activeProg.courses.map(c => <CourseCard key={c.id} course={c} programmeColor={activeProg.color} />)}
          </div>
        </div>
      )}
    </div>
  );
}
